import { memo, useMemo, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
// Import Enums
import { DisplayModeEnum, WhatsAppComponentPropertyKeyEnum, WhatsAppHeaderFormatEnum } from 'enums';
// Import Types
import type { WhatsAppTemplateComponent } from './types';
import type { WhatsAppTemplateComponentExample } from 'types';
// Import Constants
import { l, ZERO_TIME_WITHOUT_SECONDS } from 'constants/common';
import { DISPLAY_MODE_THEMES, getPreviewStyles } from './constants';
// Import Services
import { getButtonLabel, getComponentParts, getComponentProperty } from './services';

interface WhatsAppTemplatePreviewProps {
    components: Nilable<WhatsAppTemplateComponent[]>;
    displayMode?: string;
    isLoading?: boolean;
}

const WhatsAppTemplatePreview: FC<WhatsAppTemplatePreviewProps> = (props) => {
    const { components, displayMode = DisplayModeEnum.LightMode, isLoading = false } = props;
    const { t } = useTranslation();

    const theme = DISPLAY_MODE_THEMES[displayMode] ?? DISPLAY_MODE_THEMES[DisplayModeEnum.LightMode];
    const styles = useMemo(() => getPreviewStyles(theme), [theme]);
    const { header, body, footer, buttons } = useMemo(() => getComponentParts(components), [components]);

    const headerContent = useMemo((): ReactNode => {
        if (!header) return null;
        const format =
            (getComponentProperty(header, WhatsAppComponentPropertyKeyEnum.Format) as string)?.toUpperCase() ?? '';
        const text = getComponentProperty(header, WhatsAppComponentPropertyKeyEnum.Text) as Nilable<string>;

        if (format === WhatsAppHeaderFormatEnum.Text && text) {
            return <div style={styles.headerText}>{text}</div>;
        }
        if (format === WhatsAppHeaderFormatEnum.Image) {
            const example = getComponentProperty(
                header,
                WhatsAppComponentPropertyKeyEnum.Example,
            ) as Nullable<WhatsAppTemplateComponentExample>;
            const imageUrl = example?.Params?.[0]?.ParamExample;
            if (imageUrl) {
                return <img src={imageUrl} alt="Header" style={styles.headerImage} />;
            }
            return <div style={styles.headerMediaPlaceholder} title="Image" />;
        }
        if (format === WhatsAppHeaderFormatEnum.Video || format === WhatsAppHeaderFormatEnum.Document) {
            return (
                <div style={styles.headerMediaPlaceholder} title={format}>
                    <span style={styles.mediaLabel}>{format}</span>
                </div>
            );
        }
        return null;
    }, [header, styles]);

    const bodyContent = useMemo((): ReactNode => {
        const text = body
            ? (getComponentProperty(body, WhatsAppComponentPropertyKeyEnum.Text) as Nilable<string>)
            : undefined;
        return text ? <div style={styles.bodyText}>{text}</div> : null;
    }, [body, styles]);

    const footerContent = useMemo((): ReactNode => {
        const text = footer
            ? (getComponentProperty(footer, WhatsAppComponentPropertyKeyEnum.Text) as Nilable<string>)
            : undefined;
        return text ? <div style={styles.footerText}>{text}</div> : null;
    }, [footer, styles]);

    const buttonsContent = useMemo((): ReactNode => {
        const buttonList = getComponentProperty(buttons, WhatsAppComponentPropertyKeyEnum.Buttons) as Nilable<
            Array<{ Text?: string }>
        >;
        if (!Array.isArray(buttonList) || buttonList.length === 0) return null;
        return (
            <div style={styles.buttonsContainer}>
                {buttonList.map((button, index) => {
                    const label = getButtonLabel(button);
                    return label ? (
                        <div key={index} style={styles.buttonItem}>
                            {label}
                        </div>
                    ) : null;
                })}
            </div>
        );
    }, [buttons, styles]);

    if (isNil(components)) return null;

    return (
        <div style={styles.wrapper}>
            <div style={styles.template}>
                {isLoading ? (
                    <div style={styles.loadingEmpty}>{t(l.Loading)}</div>
                ) : (
                    <>
                        {isEmpty(components) ? (
                            <div style={styles.loadingEmpty}>{t(l.NoDataToDisplay)}</div>
                        ) : (
                            <>
                                {headerContent}
                                {bodyContent}
                                {footerContent}
                                {buttonsContent}
                                <div style={styles.timestamp}>{ZERO_TIME_WITHOUT_SECONDS}</div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default memo(WhatsAppTemplatePreview);
